import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import Groups2Icon from '@mui/icons-material/Groups2';
import InfoIcon from '@mui/icons-material/Info';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import {
  Alert,
  AlertTitle,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { FC } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';

import TableBodyLoader from '../../../../shared/components/TableBodyLoader/TableBodyLoader';
import reorderArrayItem from '../../../../utils/reorderArrayItem';
import Segment from '../../interfaces/Segment';
import SegmentNames from '../../interfaces/SegmentNames';
import RMFConfigTableRow from '../RFMConfigTableRow/RFMConfigTableRow';
import { useTranslations } from 'use-intl';

interface RMFConfigTableProps {
  segments: Segment[];
  loading: boolean;
  onUpdateConfig: (segments: Segment[]) => Promise<void>;
}

const rmfConfigTableHeaders = SegmentNames;

const RMFConfigTable: FC<RMFConfigTableProps> = ({
  segments,
  loading,
  onUpdateConfig,
}) => {
  const t = useTranslations('rfmConfig');
  const handleUpdateConfig = (index: number, newConfig: Segment) => {
    const updatedConfig = [...segments];
    updatedConfig[index] = newConfig;
    return onUpdateConfig(updatedConfig);
  };

  const handleDeleteSegment = (deleteConfig: Segment) => {
    const updatedConfig = segments.filter((c) => c.id !== deleteConfig.id);
    return onUpdateConfig(updatedConfig);
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const movedItems = reorderArrayItem(
      segments,
      result.source.index,
      result.destination.index
    );

    onUpdateConfig(movedItems);
  };

  if (!segments.length && !loading)
    return (
      <Alert severity='info'>
        <AlertTitle>{t('table.noSegments')}</AlertTitle>
        {t.rich('table.noSegmentsHint', { strong: (chunks) => <strong>{chunks}</strong> })}
      </Alert>
    );

  return (
    <TableContainer component={Paper}>
      <Table>
        <caption>
          {t.rich('table.caption', { br: () => <br /> })}
        </caption>
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>{t('table.name')}</TableCell>
            {rmfConfigTableHeaders.map((segment) => (
              <TableCell colSpan={2} key={segment}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}
                >
                  {t(`segmentNames.${segment}`)}
                  <Tooltip title={t(`segmentDescriptions.${segment}`)}>
                    <InfoIcon fontSize='small' />
                  </Tooltip>
                </Box>
              </TableCell>
            ))}
            <TableCell>{t('edit')}</TableCell>
            <TableCell>{t('delete')}</TableCell>
            <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
              <LowPriorityIcon fontSize='small' />
            </TableCell>
          </TableRow>
        </TableHead>
        {loading ? (
          <TableBodyLoader colSpan={11} rowsPerPage={7} />
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId='drop'>
              {(provided) => (
                <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                  <TableRow>
                    <TableCell>
                      <EmojiEventsIcon fontSize='small' color='primary' />
                    </TableCell>
                    <TableCell>
                      <Groups2Icon fontSize='small' color='primary' />
                    </TableCell>
                    <TableCell>
                      <Typography color='text.secondary' variant='body2'>
                        {t('min')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color='text.secondary' variant='body2'>
                        {t('max')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color='text.secondary' variant='body2'>
                        {t('min')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color='text.secondary' variant='body2'>
                        {t('max')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color='text.secondary' variant='body2'>
                        {t('min')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color='text.secondary' variant='body2'>
                        {t('max')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color='text.secondary' variant='body2'>
                        -
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color='text.secondary' variant='body2'>
                        -
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color='text.secondary' variant='body2'>
                        -
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {segments.map((eachConfig, index) => (
                    <Draggable
                      key={eachConfig.id}
                      draggableId={'q-' + eachConfig.id}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <RMFConfigTableRow
                          segments={segments}
                          config={eachConfig}
                          priority={index + 1}
                          draggableProvided={provided}
                          draggableStateSnapshot={snapshot}
                          onUpdate={handleUpdateConfig.bind(null, index)}
                          onDelete={handleDeleteSegment}
                        />
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Table>
    </TableContainer>
  );
};
export default RMFConfigTable;
